import { BaseAssetPath } from '@/common'
import { CopyNotify, copyText } from '@/components/copy'
import { Input } from '@/components/editor/Input'
import { CallbackWrap } from '@/types/common'
import { Embed } from '@/types/embed'
import { LevelingEmbed } from '@/types/leveling'
import { OverlayContext } from '@/ui/utils/OverlayManager'
import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Widget } from 'leveling/widget'
import React, { useMemo } from 'react'
import { HelperTooltip } from './helper-tooltip'
import './pob-links.scss'

type LevelingPob = Exclude<LevelingEmbed['pobs'], undefined>[0]

const emptyContent: LevelingEmbed['pobs'] = []

export function PobLinks({
  embed,
  onEmbedChange
}: {
  embed: LevelingEmbed
  onEmbedChange?: (value: CallbackWrap<Embed>) => void
}) {
  const content = embed?.pobs || emptyContent

  const [isEditing, setIsEditing] = React.useState(false)

  const onAdd = React.useCallback(
    () =>
      onEmbedChange?.((embed) => {
        if (embed.type !== 'leveling') return embed
        embed = { ...embed }
        embed.pobLink = undefined
        embed.pobs = embed.pobs?.slice() ?? []
        embed.pobs.push({ label: '', link: '' })
        return embed
      }),
    [onEmbedChange]
  )

  const onDelete = (index: number) =>
    onEmbedChange?.((embed) => {
      if (embed.type !== 'leveling') return embed
      embed = { ...embed }
      embed.pobs = embed.pobs?.slice() ?? []
      embed.pobs.splice(index, 1)
      return embed
    })

  const onEditAttr = <K extends keyof LevelingPob>(index: number, attr: K, newValue: LevelingPob[K]) =>
    onEmbedChange?.((embed) => {
      if (embed.type !== 'leveling') return embed
      embed = { ...embed }
      embed.pobs = embed.pobs?.slice() ?? []
      embed.pobs[index] = {
        ...embed.pobs[index],
        [attr]: newValue
      }
      return embed
    })

  const overlays = React.useContext(OverlayContext)
  const [copied, setCopied] = React.useState<string | undefined>(undefined)
  const copyPobLink = React.useCallback(
    (event: React.MouseEvent, pobLink?: string) => {
      event.preventDefault()
      event.stopPropagation()
      if (!pobLink) return
      copyText(pobLink)
      overlays.async(<CopyNotify pos={{ left: event.clientX, top: event.clientY }} />)
      setCopied(pobLink)
    },
    [overlays]
  )

  const pobs = useMemo(() => {
    if (content.length !== 0) {
      return content
    }

    if (embed?.pobLink) return [{ label: 'PoB', link: embed.pobLink }]

    return []
  }, [content, embed?.pobLink])

  return (Array.isArray(pobs) && pobs.length > 0) || onEmbedChange ? (
    <Widget
      className="poe-pobs-wrapper"
      title={
        <>
          PoBs
          {onEmbedChange &&
            (isEditing ? (
              <div>
                <button onClick={() => setIsEditing(false)}>Close</button>
              </div>
            ) : (
              <div>
                <FontAwesomeIcon
                  icon={faEdit}
                  onClick={() => {
                    setIsEditing(!isEditing)
                  }}
                />
              </div>
            ))}
          <HelperTooltip tooltip={<img src={`${BaseAssetPath}/images/poe/misc/pob-tooltip.webp`} />} />
        </>
      }>
      <div className="poe-pobs">
        {isEditing ? (
          <div className="poe-pobs-edit">
            {content.map((item, index) => (
              <div key={index}>
                <Input
                  placeholder="PoB Label"
                  value={item.label ?? ''}
                  onChange={
                    onEmbedChange
                      ? (newValue) => {
                          onEditAttr(index, 'label', newValue)
                        }
                      : undefined
                  }
                />

                <Input
                  placeholder="PoB Link"
                  value={item.link ?? ''}
                  onChange={
                    onEmbedChange
                      ? (newValue) => {
                          onEditAttr(index, 'link', newValue)
                        }
                      : undefined
                  }
                />

                <FontAwesomeIcon
                  icon={faTrashAlt}
                  onClick={() => {
                    onDelete(index)
                  }}
                />
              </div>
            ))}
            <button onClick={onAdd}>Add</button>
          </div>
        ) : (
          pobs.map((item, index) => {
            return (
              <div key={index} className="poe-pob">
                <span className="poe-pob-left">
                  <span>{item.label}:</span>
                  <span
                    className="poe-link-text"
                    onClick={(event) => {
                      copyPobLink(event, item?.link)
                    }}>
                    {item?.link}
                  </span>
                </span>

                {copied === item?.link ? <span className="poe-link-copied">-&nbsp;&nbsp;Copied</span> : null}
              </div>
            )
          })
        )}
      </div>
    </Widget>
  ) : null
}
